import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ArrowLeft,
  RefreshCw,
  AreaChart,
  Target,
  Languages,
  Brain,
  ArrowUp,
  ArrowDown,
  ChartNoAxesCombined,
  Download,
} from "lucide-react";
// Chart.js imports
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);
import { dataService, ACCESS_LEVELS } from '@rti/shared';
import { trackUserEvent } from "@/utils/userTracking";
import DriversExportButton from "@/components/DriversExportButton";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { isDemoMode } from "@/demo/demoState";
import { hasAccess } from "@/utils/commonUtils";

const chemOrBisCreditList = ['PE', 'PP', 'ABS', 'PET'];
const customSortOrder = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];

const ReportData: React.FC<{
  content: string;
  title?: string;
  type: string;
  className?: string;
}> = ({ content, title, type, className = "" }) => {
  const getIcon = () => {
    switch (type) {
      case 'marketOverview': return <AreaChart className="mr-3 text-rs-green" />;
      case 'supplyDemand': return <AreaChart className="mr-3 text-rs-green" />;
      case 'feedstocks': return <AreaChart className="mr-3 text-rs-green" />;
      case 'actionStrategies': return <Target className="mr-3 text-rs-green" />;
      case 'international': return <Languages className="mr-3 text-rs-green" />;
      case 'title': return <Brain className="mr-3 text-rs-green" />;
      default: return null;
    }
  };

  const getSectionTitle = () => {
    switch (type) {
      case 'marketOverview': return title || 'Market Overview';
      case 'supplyDemand': return title || 'Supply/Demand';
      case 'feedstocks': return title || 'Feedstocks';
      case 'international': return title || 'International';
      case 'actionStrategies': return title || 'Action Strategies';
      default: return title || '';
    }
  };

  const formatContent = (rawText: string) => {
    if (!rawText || rawText.length === 0) return '';
    return rawText
      .replace(/\n\n/g, '<br><br>')
      .replace(/\r\n\r\n/g, '<br><br>')
      .replace(/\r\n/g, '<br>')
      .replace(/\n/g, '<br>')
      .replace(/\x0b/g, '<br>')
      .replace(/\r\?/g, '<br>-')
      .replace(/\?/g, '-')
      .replace(/\r\r/g, '<br><br>')
      .replace(/\r/g, '<br><br>');
  };

  if (!content || content.length === 0) return null;

  return (
    <div className={`p-7 bg-rs-off-black rounded-lg ${type !== 'actionStrategies' ? 'h-full' : ''} ${className}`}>
      <h3 className={`flex items-center text-lg ${content.length > 0 ? 'mb-3' : 'mb-0'}`}>
        {getIcon()}
        {getSectionTitle()}
      </h3>
      <p
        className="text-sm font-light"
        dangerouslySetInnerHTML={{ __html: formatContent(content) }}
      />
    </div>
  );
};

// The first weekly report of a month finalizes the prior month's numbers, e.g.
// "Supply/Demand: Final February Data:". We never show that box in the demo.
const isFinalDataTitle = (title?: string) => !!title && /final.*data/i.test(title);

const BlurInDemo: React.FC<{ active: boolean; className?: string; children: React.ReactNode }> = ({
  active,
  className = "",
  children,
}) => (
  <div className={`relative ${className}`}>
    <div className={active ? "blur-md pointer-events-none select-none" : ""}>{children}</div>
    {active && (
      <div className="absolute inset-0 flex items-center justify-center bg-rs-black/50 rounded-lg">
        <p className="text-sm font-semibold text-rs-off-white text-center px-4">
          Supply/Demand: Final Monthly Data available to ResinSmart members
        </p>
      </div>
    )}
  </div>
);

const Drivers: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const isOnTrial = !!(user?.on_free_trial && !user?.subscription_status?.toLowerCase().match(/^(active|trialing)$/));
  const userLevel = user?.effective_membership_id ?? 0;
  const restrictWeeklyHistoryToOneYear = !hasAccess(userLevel, ACCESS_LEVELS.COMPOUND);
  const [needsLoading, setNeedsLoading] = useState(true);
  const [spotLoading, setSpotLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [driver, setDriver] = useState('');
  const [trends, setTrends] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [showTrend, setShowTrend] = useState(false);
  const [showChemOrbisCredit, setShowChemOrbisCredit] = useState(false);
  const [reports, setReports] = useState<any[]>([]);
  const [filteredReports, setFilteredReports] = useState<any[]>([]);
  const [activeDriverReport, setActiveDriverReport] = useState('');
  const [feedstockData, setFeedstockData] = useState<any[]>([]);
  const [groupedFeedstockData, setGroupedFeedstockData] = useState<any>({});
  const [hasBarChart, setHasBarChart] = useState(false);
  const [spotData, setSpotData] = useState<any[]>([]);
  const [spotHistory, setSpotHistory] = useState<
    Record<string, { feeds: { label: string; measure: string }[]; rows: { date: string; values: (string | null)[] }[] }>
  >({});

  const barChartRef = useRef<HTMLCanvasElement>(null);
  const lineChartRef = useRef<HTMLCanvasElement>(null);
  const barChartInstance = useRef<Chart | null>(null);
  const lineChartInstance = useRef<Chart | null>(null);
  const generatingChartsRef = useRef(false);

  // Utility functions
  const convertValue = (convert: string, price: number) => {
    const metricTonToLb = 2204.62;
    if (convert === "true") {
      return price / metricTonToLb;
    } else if (convert === "reverse") {
      return price * metricTonToLb;
    } else {
      return price;
    }
  };

  const formatDate = (date: string) => {
    const utcDate = new Date(date + 'T00:00:00Z');
    return utcDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  // Full history spans multiple years, so include the year in the row label
  const formatFullDate = (date: string) => {
    const utcDate = new Date(date + 'T00:00:00Z');
    return utcDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const buildSpotHistory = (
    historyFeeds: { title: string; feed: any; data: any[] }[]
  ): Record<string, { feeds: { label: string; measure: string }[]; rows: { date: string; values: (string | null)[] }[] }> => {
    // Map any date to the Friday of its week (week ending Friday). Source feeds
    // store on different weekdays (petchem=daily, chemorbis=Mon, capro=monthly),
    // so we bucket every row onto a common weekly (Friday) grid.
    const weekFridayIso = (iso: string): string | null => {
      const d = new Date(iso + 'T00:00:00Z');
      if (isNaN(d.getTime())) return null;
      const add = (5 - d.getUTCDay() + 7) % 7;
      d.setUTCDate(d.getUTCDate() + add);
      return d.toISOString().split('T')[0];
    };
    const formatFeedValue = (feed: any, raw: any): string | null => {
      if (raw === null || raw === undefined || raw === '' || isNaN(Number(raw))) return null;
      const conv = Number(convertValue(feed.mt2lb_convert, Number(raw)));
      if (isNaN(conv)) return null;
      return feed.measure === '%' ? `${conv.toFixed(2)}%` : `$${conv.toFixed(2)}`;
    };

    const byTitle: Record<
      string,
      { feeds: { label: string; measure: string }[]; rows: { date: string; values: (string | null)[] }[] }
    > = {};

    const titles: string[] = [];
    for (const { title } of historyFeeds) {
      if (!titles.includes(title)) titles.push(title);
    }

    for (const title of titles) {
      const items = historyFeeds.filter((h) => h.title === title);
      const feeds = items.map((it) => ({
        label: it.feed.measure === '%' ? it.feed.name : `${it.feed.name} ($/${it.feed.measure})`,
        measure: it.feed.measure,
      }));
      const perFeed = items.map((it) => {
        // Per week-Friday bucket, keep the value from the latest actual row in that week
        const best = new Map<string, { srcDate: string; value: string }>();
        for (const row of it.data) {
          const iso = row?.date?.split('T')[0];
          if (!iso) continue;
          const fri = weekFridayIso(iso);
          if (!fri) continue;
          const val = formatFeedValue(it.feed, row[it.feed.price]);
          if (val === null) continue;
          const existing = best.get(fri);
          if (!existing || iso > existing.srcDate) best.set(fri, { srcDate: iso, value: val });
        }
        const m = new Map<string, string>();
        best.forEach((v, fri) => m.set(fri, v.value));
        return m;
      });
      const allDates = new Set<string>();
      perFeed.forEach((m) => m.forEach((_v, iso) => allDates.add(iso)));
      let rows = Array.from(allDates)
        .sort((a, b) => b.localeCompare(a))
        .map((date) => ({ date, values: perFeed.map((m) => m.get(date) ?? null) }));

      // Forward-fill only Caprolactam (monthly data): if a cell is null, use the last non-null value
      const caproColIdx = feeds.findIndex((f) => f.label.toLowerCase().includes('caprolactam'));
      if (caproColIdx >= 0) {
        const rowsReversed = [...rows].reverse();
        let lastCaproValue: string | null = null;
        for (const row of rowsReversed) {
          if (row.values[caproColIdx] !== null) {
            lastCaproValue = row.values[caproColIdx];
          } else if (lastCaproValue !== null) {
            row.values[caproColIdx] = lastCaproValue;
          }
        }
        rows = rowsReversed.reverse();
      }

      // Base tier sees only the last 12 months, in both the table and its CSV export
      // (the export reads from this same rows array). Compound/Prime/Employee see all.
      if (restrictWeeklyHistoryToOneYear) {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        const cutoffIso = oneYearAgo.toISOString().split('T')[0];
        rows = rows.filter((row) => row.date >= cutoffIso);
      }

      byTitle[title] = { feeds, rows };
    }
    return byTitle;
  };

  const exportSpotHistoryCSV = (title: string, hist: any) => {
    const headers = ['Week (Fri)', ...hist.feeds.map((f: any) => f.label)];
    const dataRows = hist.rows.map((row: any) => [
      row.date,
      ...row.values.map((v: string | null) => v ?? ''),
    ]);
    const csvContent = [headers.join(','), ...dataRows.map((row: string[]) => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `spot-feeds-${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const processEntryValue = (value: string) => {
    let processed = value.replace(/ to [+-]$/, ' to');
    return processed.replace('-', '');
  };

  const groupBy = (array: any[], key: string) => {
    return array.reduce((result, item) => {
      (result[item[key]] = result[item[key]] || []).push(item);
      return result;
    }, {});
  };

  const handleDateSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    trackUserEvent("select_driver_date", "Driver Date Changed", {
      date: newDate,
    });
  };

  // Fetch driver data on mount
  useEffect(() => {
    setNeedsLoading(true);
    trackUserEvent("view_drivers_page", "Drivers Page Viewed");

    dataService.getDriverData()
      .then(response => {
        // Handle normalized data structure from API client
        const data = response || [];

        if (Array.isArray(data)) {
          if (data.length > 0) {
            setReports(data);
            const dates = availableDates(data);
            if (dates.length > 0) {
              setSelectedDate(dates[0]);
            }
          }
        }
        setNeedsLoading(false);
      })
      .catch(error => {
        setNeedsLoading(false);
      });
  }, []);

  // Helper: group reports by date
  const groupedReports = useCallback((data = reports) => {
    if (!Array.isArray(data)) return {};
    const grouped = data.reduce((acc: any, report: any) => {
      if (!report.date) return acc;
      const parsedDate = new Date(report.date);
      if (isNaN(parsedDate.getTime())) return acc;
      const formattedDate = parsedDate.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
      });
      if (!acc[formattedDate]) acc[formattedDate] = [];
      acc[formattedDate].push(report);
      return acc;
    }, {});

    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a: any, b: any) => {
        const aAbbr = getDriverReportAbbreviation(a.driverReport);
        const bAbbr = getDriverReportAbbreviation(b.driverReport);
        return customSortOrder.indexOf(aAbbr) - customSortOrder.indexOf(bAbbr);
      });
    });
    return grouped;
  }, [reports]);

  // Helper: get available dates
  const availableDates = (data = reports) => {
    return Object.keys(groupedReports(data)).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  };

  // Watch selectedDate changes
  useEffect(() => {
    if (!selectedDate) return;
    const grouped = groupedReports();
    const allReportsForDate = grouped[selectedDate] || [];

    const validReports = allReportsForDate.filter((report: any) => {
      const driverAbbr = getDriverReportAbbreviation(report.driverReport);
      return driverAbbr && customSortOrder.includes(driverAbbr);
    });

    setFilteredReports(validReports);

    if (validReports.length > 0) {
      setActiveDriverReport(validReports[0].driverReport);
      filterReportsByDriver(selectedDate, validReports[0].driverReport);
    } else {
      setActiveDriverReport('');
    }
    showTrends(selectedDate);
  }, [selectedDate, reports]);

  // Filter reports by driver
  const filterReportsByDriver = (date: string, driverReport: string) => {
    trackUserEvent("select_driver_report", `Driver Report Selected: ${driverReport}`);
    const grouped = groupedReports();
    const filtered = (grouped[date] || []).filter((report: any) => report.driverReport === driverReport);
    setFilteredReports(filtered);
    setActiveDriverReport(driverReport);
    const abbreviation = getDriverReportAbbreviation(driverReport);
    setDriver(abbreviation);
    setHasBarChart(abbreviation === 'PET');
    setShowChemOrbisCredit(chemOrBisCreditList.includes(abbreviation));

    // Clear existing chart data to ensure re-render
    setChartData([]);
    setSpotData([]);
    setFeedstockData([]);
    setGroupedFeedstockData({});

    // Load driver data with the updated selection
    loadDriverData(abbreviation, date);
  };

  // Load driver chart and spot data
  const loadDriverData = async (driverCode: string, date?: string) => {
    setChartLoading(true);
    setSpotLoading(true);

    try {
      // Load driver charts data
      const driverChartsResponse = await dataService.getDriverChartsData();

      // Handle normalized data structure
      const driverChartsData = driverChartsResponse || [];

      if (Array.isArray(driverChartsData) && driverChartsData.length > 0) {
        const driverObj = driverChartsData.find((d: any) => d.driver === driverCode);

        if (driverObj) {
          const rawCharts = driverObj.chart;
          let chartsArray: any[] = [];
          if (Array.isArray(rawCharts)) {
            chartsArray = rawCharts.slice();
          } else if (rawCharts && typeof rawCharts === 'object') {
            // Some feeds may return an object keyed by id/title
            chartsArray = Object.values(rawCharts);
          }
          setChartData(chartsArray); // new ref
          setSpotData(Array.isArray(driverObj.spot) ? [...driverObj.spot] : []);
          fetchDriverSpotData(Array.isArray(driverObj.spot) ? driverObj.spot : [], date);
          if (!chartsArray.length) {
            setChartLoading(false);
          }
        } else {
          setChartData([]);
          setSpotData([]);
          setChartLoading(false);
          setSpotLoading(false);
        }
      } else {
        setChartData([]);
        setSpotData([]);
        setChartLoading(false);
        setSpotLoading(false);
      }
    } catch (error) {
      setChartData([]);
      setSpotData([]);
      setChartLoading(false);
      setSpotLoading(false);
    }
  };

  // Generate charts
  const generateDriverCharts = async (charts: any[], attempt = 1, date?: string) => {
    if (generatingChartsRef.current) {
      return;
    }
    if (!charts || charts.length === 0) {
      setChartLoading(false);
      return;
    }
    const barNeeded = hasBarChart;
    const canvasReady = (barNeeded ? !!barChartRef.current : true) && !!lineChartRef.current;
    if (!canvasReady) {
      if (attempt <= 5) {
        setTimeout(() => generateDriverCharts(charts, attempt + 1, date), 60 * attempt);
      } else {
        setChartLoading(false);
      }
      return;
    }
    generatingChartsRef.current = true;

    // Destroy old instances once at start
    if (barChartInstance.current) { barChartInstance.current.destroy(); barChartInstance.current = null; }
    if (lineChartInstance.current) { lineChartInstance.current.destroy(); lineChartInstance.current = null; }

    for (const chart of charts) {
      const isBar = chart.type === 'bar' && barChartRef.current;
      const targetRef = isBar ? barChartRef.current : lineChartRef.current;
      if (!targetRef) continue;

      const numData = chart.type === 'bar' ? 11 : 4;
      const chartAnchor = date ? new Date(date) : new Date();
      const labels = Array.from({ length: numData }, (_, i) => {
        const month = chartAnchor.getMonth() - i;
        const year = month < 0 ? chartAnchor.getFullYear() - 1 : chartAnchor.getFullYear();
        const adjustedMonth = (month + 12) % 12;
        return new Date(year, adjustedMonth).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      }).reverse();

      const datasets: any[] = [];
      const lineColors = ['#42b200', '#3838ff', '#854cff', '#e85b4c', '#e880d9'];
      if (chart.values) {
        for (let i = 0; i < chart.values.length; i++) {
          const value = chart.values[i];
          if (value?.source && value.source.length > 2) {
            try {
              const sourceResponse = await dataService.getDataByFilename(value.source);
              const sourceData = sourceResponse || [];
              if (Array.isArray(sourceData) && sourceData.length > 0) {
                const data = getLastAvailableDays(sourceData, numData, chartAnchor).map((item: any) => convertValue(value.mt2lb_convert, item[value.price]));
                datasets.push({
                  label: value.symbol,
                  data,
                  borderColor: lineColors[i % lineColors.length],
                  backgroundColor: lineColors[i % lineColors.length],
                  fill: false,
                  pointRadius: 1,
                  pointBackgroundColor: lineColors[i % lineColors.length],
                  pointBorderColor: lineColors[i % lineColors.length],
                  yAxisID: chart.dualaxis && value.symbol.includes('BZ') ? 'y2' : 'y1'
                });
              }
            } catch (e) {
              // Error handled silently
            }
          }
        }
      }

      try {
        const cfg: any = {
          type: isBar ? 'bar' : 'line',
          data: { labels, datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: !isBar,
                labels: !isBar ? {
                  usePointStyle: true,
                  pointStyle: 'circle',
                  color: '#DEE7D9',
                  font: {
                    size: 12,  // Adjust title font size
                    weight: 'normal'
                  },
                  padding: 20,
                  position: 'top',
                  align: 'left'
                } : undefined
              },
              title: { display: true, text: chart.title, font: { size: 14, weight: 'normal' }, color: isDark ? '#DEE7D9' : '#1e2019', padding: { top: 5, bottom: isBar ? 20 : 0 } },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: isDark ? '#DEE7D9' : '#1e2019' }, grid: { lineWidth: 1, color: isDark ? 'rgba(222,231,217,.1)' : 'rgba(0,0,0,.1)', borderDash: [5,5] } },
              y1: { type: 'linear', position: 'left', ticks: { color: isDark ? '#DEE7D9' : '#1e2019', callback: (v: any) => '$' + Number(v).toFixed(2) }, grid: { lineWidth: 1, color: isDark ? 'rgba(222,231,217,.1)' : 'rgba(0,0,0,.1)', borderDash: [5,5] } },
              y2: { type: 'linear', position: 'right', display: datasets.some(d => d.yAxisID === 'y2'), ticks: { color: isDark ? '#DEE7D9' : '#1e2019', display: datasets.some(d => d.yAxisID === 'y2'), callback: (v: any) => '$' + Number(v).toFixed(2) }, grid: { display: false, drawOnChartArea: false } }
            }
          }
        };
        if (isBar) {
          barChartInstance.current = new Chart(targetRef, cfg);
        } else {
          lineChartInstance.current = new Chart(targetRef, cfg);
        }
      } catch (e) {
        // Error handled silently
      }
    }
    generatingChartsRef.current = false;
    setChartLoading(false);
  };

  // Get last available days for chart data — anchorDate is the ceiling month (inclusive)
  const getLastAvailableDays = (data: any[], numData: number, anchorDate: Date = new Date()) => {
    const result = [];

    for (let i = 0; i < numData; i++) {
      const month = anchorDate.getMonth() - i;
      const year = month < 0 ? anchorDate.getFullYear() - 1 : anchorDate.getFullYear();
      const adjustedMonth = (month + 12) % 12;

      const filteredData = data.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate.getFullYear() === year && itemDate.getMonth() === adjustedMonth;
      });

      if (filteredData.length > 0) {
        const lastDay = filteredData.reduce((latest, current) => {
          return new Date(current.date) > new Date(latest.date) ? current : latest;
        });
        result.push(lastDay);
      }
    }

    return result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  // Fetch driver spot data
  const fetchDriverSpotData = async (spots: any[], date?: string) => {
    setSpotLoading(true);
    setSpotHistory({});
    const result: any[] = [];
    const historyFeeds: { title: string; feed: any; data: any[] }[] = [];

    // Resolve the anchor date: use the selected date if provided, otherwise today
    const anchorDate = date ? new Date(date) : new Date();

    const getRecentFridays = (isRefinery: boolean) => {
      const dayOfWeek = anchorDate.getDay();
      const daysSinceFriday = dayOfWeek >= 5 ? dayOfWeek - 5 : dayOfWeek + 2;

      let lastFriday = new Date(anchorDate);
      lastFriday.setDate(anchorDate.getDate() - daysSinceFriday);

      const fridays = [];
      if (isRefinery) {
        const twoFridaysAgo = new Date(lastFriday);
        twoFridaysAgo.setDate(lastFriday.getDate() - 7);
        const threeFridaysAgo = new Date(twoFridaysAgo);
        threeFridaysAgo.setDate(twoFridaysAgo.getDate() - 7);
        fridays.push(twoFridaysAgo.toISOString().split('T')[0]);
        fridays.push(threeFridaysAgo.toISOString().split('T')[0]);
      } else {
        const secondLastFriday = new Date(lastFriday);
        secondLastFriday.setDate(lastFriday.getDate() - 7);
        fridays.push(lastFriday.toISOString().split('T')[0]);
        fridays.push(secondLastFriday.toISOString().split('T')[0]);
      }
      return fridays;
    };

    for (const spot of spots) {
      for (const feed of spot.feeds) {
        try {
          const recentFridays = getRecentFridays(feed.source.includes("refinery"));
          const feedResponse = await dataService.getDataByFilename(feed.source);

          // Handle normalized data structure
          const feedData = feedResponse || [];
          historyFeeds.push({ title: spot.title, feed, data: Array.isArray(feedData) ? feedData : [] });

          if (Array.isArray(feedData) && feedData.length > 0) {
            // Compare ISO date strings directly to avoid timezone-parsing mismatches
            // (feed data dates are "YYYY-MM-DD" strings; Date objects can shift by timezone).
            // recentFridays already contains the correct ISO strings for the selected date.
            let filteredData = feedData
              .filter((item: any) => recentFridays.includes(item.date?.split('T')[0]))
              .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

            // Fallback: two most recent records on or before the anchor date
            if (filteredData.length < 2) {
              const pad = (n: number) => String(n).padStart(2, '0');
              const anchorIso = `${anchorDate.getFullYear()}-${pad(anchorDate.getMonth() + 1)}-${pad(anchorDate.getDate())}`;
              const sorted = [...feedData]
                .filter((item: any) => (item.date?.split('T')[0] ?? '') <= anchorIso)
                .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
              filteredData = sorted.slice(0, 2);
            }

            if (filteredData.length > 2) {
              filteredData = filteredData.slice(0, 2);
            }

            const value1 = filteredData[0] && !isNaN(filteredData[0][feed.price])
              ? feed.measure === '%'
                ? `${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[0][feed.price]))).toFixed(2)}%`
                : `$${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[0][feed.price]))).toFixed(2)}`
              : null;

            const value2 = filteredData[1] && !isNaN(filteredData[1][feed.price])
              ? feed.measure === '%'
                ? `${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[1][feed.price]))).toFixed(2)}%`
                : `$${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[1][feed.price]))).toFixed(2)}`
              : null;

            const strippedValue1 = value2 ? parseFloat(value2.replace(/[$%]/g, '')) : null;
            const strippedValue2 = value1 ? parseFloat(value1.replace(/[$%]/g, '')) : null;

            const change = strippedValue1 !== null && strippedValue2 !== null
              ? feed.measure === '%'
                ? `${(strippedValue2 - strippedValue1).toFixed(2)}%`
                : `$${(strippedValue2 - strippedValue1).toFixed(2)}`
              : 'N/A';

            result.push({
              title: spot.title,
              feedName: feed.measure === '%' ? feed.name : `${feed.name} ($/${feed.measure})`,
              measure: feed.measure,
              data: filteredData,
              date1: formatDate(recentFridays[1]),
              date2: formatDate(recentFridays[0]),
              value1: value2,
              value2: value1,
              change
            });
          }
        } catch (error) {
          // Error handled silently
        }
      }
    }

    const sortedData = result.sort((a, b) => a.title.localeCompare(b.title));
    setFeedstockData(sortedData);
    setGroupedFeedstockData(groupBy(sortedData, 'title'));
    setSpotHistory(buildSpotHistory(historyFeeds));
    setSpotLoading(false);
  };

  // Helper: get abbreviation
  const getDriverReportAbbreviation = (driverReport: string) => driverReport.split(' ')[0];

  // Helper: is active button
  const isActiveButton = (driverReport: string) => activeDriverReport === driverReport;

  const toIso = (humanDate: string): string => {
    const d = new Date(humanDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const showTrends = (date: string) => {
    if (!date || !availableDates().includes(date)) return;
    const localSelected = new Date(date);
    const selectedYear = localSelected.getFullYear();
    const selectedMonth = localSelected.getMonth() + 1;
    const selectedIso = toIso(date);
    const monthPrefix = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-`;

    const datesInMonthIso = availableDates()
      .filter(d => {
        const ld = new Date(d);
        return ld.getFullYear() === selectedYear && ld.getMonth() + 1 === selectedMonth;
      })
      .map(toIso)
      .sort();
    const isFirstDateInMonth = datesInMonthIso.length > 0 && datesInMonthIso[0] === selectedIso;

    dataService.getDriverPriceChanges()
      .then(data => {
        const sourceData = data || [];
        if (!Array.isArray(sourceData) || sourceData.length === 0) {
          setShowTrend(false);
          setTrends([]);
          return;
        }

        const polymerTrends = sourceData[0] || {};
        // Find every distinct entry date in the current month across polymers.
        const currentMonthEntryDates = new Set<string>();
        for (const polymer of Object.keys(polymerTrends)) {
          for (const entry of polymerTrends[polymer] || []) {
            const entryDate: string = entry?.date || '';
            if (entryDate.startsWith(monthPrefix)) {
              currentMonthEntryDates.add(entryDate);
            }
          }
        }

        // No data for current month → never show (rule 1).
        if (currentMonthEntryDates.size === 0) {
          setShowTrend(false);
          setTrends([]);
          return;
        }

        // Apply the per-entry date rules (rule 2).
        let triggerFires = false;
        for (const entryDate of currentMonthEntryDates) {
          const day = Number(entryDate.slice(8, 10));
          if (day === 1) {
            if (isFirstDateInMonth) { triggerFires = true; break; }
          } else if (entryDate === selectedIso) {
            triggerFires = true;
            break;
          }
        }

        setShowTrend(triggerFires);
        if (!triggerFires) {
          setTrends([]);
          return;
        }

        // Build the previous-4-months display.
        setTrends(sourceData.map(trend => {
          const sortedTrend: any = {};
          for (const key in trend) {
            if (trend.hasOwnProperty(key)) {
              sortedTrend[key] = trend[key]
                .filter((entry: any) => {
                  const [entryYear, entryMonth] = entry.date.split('-').map(Number);
                  const monthsDiff = (selectedYear - entryYear) * 12 + (selectedMonth - entryMonth);
                  return monthsDiff >= 0 && monthsDiff < 4;
                })
                .sort((a: any, b: any) => a.date.localeCompare(b.date))
                .map((entry: any) => {
                  const [y, m] = entry.date.split('-').map(Number);
                  const monthDate = new Date(y, m - 2, 1);
                  return {
                    ...entry,
                    date: monthDate.toLocaleString('en-US', { month: 'short' })
                  };
                });
            }
          }
          return sortedTrend;
        }));
      });
  };

  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }
      if (lineChartInstance.current) {
        lineChartInstance.current.destroy();
      }
    };
  }, []);

  // Trigger chart generation when data arrives
  useEffect(() => {
    if (chartData.length > 0) {
      generateDriverCharts(chartData, 1, selectedDate || undefined);
    } else if (!chartLoading) {
      if (barChartInstance.current) { barChartInstance.current.destroy(); barChartInstance.current = null; }
      if (lineChartInstance.current) { lineChartInstance.current.destroy(); lineChartInstance.current = null; }
    } else if (chartData.length === 0 && chartLoading) {
      // fallback safety timeout
      const t = setTimeout(() => {
        if (chartData.length === 0) {
          setChartLoading(false);
        }
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [chartData, driver, hasBarChart, selectedDate, isDark]);

  // UI rendering
  return (
    <div id="dashboard" className="drivers-page">
      <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full" style={{ display: needsLoading ? 'flex' : 'none' }}>
        <RefreshCw className="spinner !text-7xl opacity-30" />
      </div>

      <div className="h-full flex flex-col p-5 w-full dashboard-shadow max-w-8xl w-full mt-5" style={{ display: needsLoading ? 'none' : 'block' }}>
        <div className="content-toolbar">
          <button className="jet" onClick={() => window.location.href = '/dashboard'}>
            <ArrowLeft className="inline" />&ensp;Back to Landing Page
          </button>
        </div>

        <div className="drivers-reports grid grid-cols-1">
          <div className="pt-5 pb-1 flex justify-between items-center" id="drivers-header">
            <div className="col-start-1 col-end-10">
              <label className="mr-10 pr-4 pt-4 text-3xl font-light" htmlFor="date-select">Weekly Market Drivers</label>
              {isOnTrial ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-rs-off-white/80 text-sm">{selectedDate}</span>
                  <span className="text-rs-off-white/40 text-xs italic">
                    Upgrade to a paid subscription to see past drivers
                  </span>
                </div>
              ) : (
                <select
                  className="px-4 py-2 bg-rs-green !text-black font-medium rounded-md transition-all hover:bg-rs-green/80 focus:outline-none"
                  style={{ colorScheme: 'dark' }}
                  id="date-select"
                  data-testid="date-select"
                  value={selectedDate || ''}
                  onChange={handleDateSelectChange}
                >
                  {availableDates().map(date => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
              )}
            </div>
            <DriversExportButton
              selectedDate={selectedDate}
              driver={driver}
              driver_report_content={activeDriverReport}
              filteredReports={filteredReports}
              visibleReports={((selectedDate ? groupedReports()[selectedDate] : []) || []).filter((report: any) => {
                const driverAbbr = getDriverReportAbbreviation(report.driverReport);
                return driverAbbr && customSortOrder.includes(driverAbbr);
              })}
              trends={showTrend ? trends : []}
              disabled={needsLoading}
              lockedForTrial={isOnTrial}
            />
          </div>
          <hr className="my-3 pb-5 -mx-5 border-rs-off-black" />

          {selectedDate && (
            <div>
              <div className="button-row pb-7">
                {(groupedReports()[selectedDate] || []).map((report: any) => {
                  const driverAbbr = getDriverReportAbbreviation(report.driverReport);
                  // Only show button if abbreviation exists and is in customSortOrder
                  if (!driverAbbr || !customSortOrder.includes(driverAbbr)) {
                    return null;
                  }

                  return (
                    <button
                      key={report.driverReport}
                      data-testid={`driver-button-${driverAbbr}`}
                      className={`p-3 rounded-sm text-sm mb-2 transition-colors ${isActiveButton(report.driverReport) ? 'bg-rs-green !text-black' : 'secondary'}`}
                      onClick={() => filterReportsByDriver(selectedDate, report.driverReport)}
                      title={`Click to view ${driverAbbr} report`}
                    >
                      {driverAbbr}
                    </button>
                  );
                })}
              </div>

              {filteredReports.length > 0 && (
                <div>
                  {filteredReports.map((report: any, idx: number) => (
                    <div key={idx}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 items-stretch" id="driver-report-data">
                        {/* Trends Section */}
                        {showTrend && trends.length > 0 && trends[0][getDriverReportAbbreviation(report.driverReport)] && (
                          <div className="col-span-1 p-7 lg:col-span-4 bg-rs-off-black rounded-lg h-full grid grid-cols-4 items-center justify-center">
                            <div className="col-span-4 flex items-center justify-center mb-3">
                              <ChartNoAxesCombined className="mr-3 !text-2xl text-rs-green" />
                              <span className="font-light text-lg">Monthly Price Trends</span>
                              <ChartNoAxesCombined className="ml-3 !text-2xl text-rs-green" />
                            </div>
                            <div className="flex items-center justify-center col-span-4">
                              <div className="flex flex-col max-w-5xl w-full">
                                <div className="grid grid-cols-5 gap-4 items-center border-b-1 border-rs-jet-light pb-3">
                                  <div className="flex items-center justify-center">{selectedDate}</div>
                                  {trends[0][getDriverReportAbbreviation(report.driverReport)].map((entry: any, i: number) => (
                                    <div key={i} className="flex items-center justify-center">{entry.date}</div>
                                  ))}
                                </div>
                                <div className="grid grid-cols-5 gap-4 items-center pt-3">
                                  <div className="flex items-center justify-center">Price Change</div>
                                  {trends[0][getDriverReportAbbreviation(report.driverReport)].map((entry: any, i: number) => (
                                    <div key={i} className="flex items-center justify-center">
                                        {(entry.value.includes('-') && !entry.value.includes('to')) && (
                                            <ArrowDown className="text-rs-red" />
                                        )}
                                        {(!entry.value.includes('-') && !entry.value.includes('Flat')) && (
                                            <ArrowUp className="text-rs-green" />
                                        )}
                                      <span>{processEntryValue(entry.value)}</span>
                                      {(entry.value.includes('-') && entry.value.includes('to')) && (
                                        <ArrowDown className="text-rs-red" />
                                      )}
                                      {(entry.value.includes('+') && entry.value.includes('to')) && (
                                        <ArrowUp className="text-rs-green" />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Title Section */}
                        <div className="col-span-1 sm:col-span-2 lg:col-span-4 p-7 bg-rs-off-black rounded-lg h-full">
                          <h3 className="flex items-center justify-center text-center lg:text-xl mb-0">
                            <Brain className="mr-3 !text-4xl text-rs-green" />
                            {report.title}
                            <Brain className="ml-3 !text-4xl text-rs-green flipped" />
                          </h3>
                        </div>

                        {/* Market Overview */}
                        <ReportData
                          className="col-span-1 lg:col-span-2"
                          content={isOnTrial && report.marketOverviewBodyTrial ? report.marketOverviewBodyTrial : report.marketOverviewBody}
                          type="marketOverview"
                        />

                        {/* Action Strategies */}
                        {report.actionStrategies && (
                          <ReportData
                            className="col-span-1 lg:col-span-2 h-full"
                            content={report.actionStrategies}
                            title={`RTI ${getDriverReportAbbreviation(report.driverReport)} Outlook and Suggested Action Strategies`}
                            type="actionStrategies"
                          />
                        )}

                        {/* Conditional Feedstocks/International when no action strategies and no supply/demand */}
                        {!report.actionStrategies && (!report.supplyDemandBody || isOnTrial) && (
                          <>
                            <ReportData
                              className="col-span-1 lg:col-start-3"
                              content={report.feedstocksBody}
                              type="feedstocks"
                            />
                            <ReportData
                              className="col-span-1"
                              content={report.internationalBody}
                              type="international"
                            />
                          </>
                        )}
                      </div>

                      {(() => {
                        const sdContent = isOnTrial ? null : report.supplyDemandBody;
                        return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                        {/* Spot Data Section */}
                        <div id="spot-data-section" className="flex flex-col items-center justify-center py-7 px-4 bg-rs-off-black rounded-lg col-span-2 md:col-span-1">
                          {(spotLoading || Object.keys(groupedFeedstockData).length <= 0) ? (
                            <RefreshCw className="spinner !text-7xl opacity-30" />
                          ) : (
                            Object.entries(groupedFeedstockData).map(([title, feedstocks]: [string, any]) => (
                              <div key={title} className="mb-8">
                                {/* Header Row */}
                                <div className={`grid grid-cols-special lg:grid_cols_special gap-4 text-center ${isDark ? 'text-white' : 'text-black'} rounded-t py-2 border-b-1 border-rs-jet text-sm`}>
                                  <div className="font-bold text-left">{title}</div>
                                  <div>{feedstocks[0]?.date1 || ''}</div>
                                  <div>{feedstocks[0]?.date2 || ''}</div>
                                  <div>(CHG)</div>
                                </div>

                                {/* Body Rows */}
                                {feedstocks.map((feedstock: any, i: number) => (
                                  <div key={i} className={`grid grid-cols-special gap-4 text-center text-xs border-b-1 border-rs-jet px-2 py-2 ${isDark ? 'text-white' : 'text-black'}`}>
                                    <div className="text-left">{feedstock.feedName}</div>
                                    <div>{feedstock.value1 || '—'}</div>
                                    <div>{feedstock.value2 || '—'}</div>
                                    <div className={`text-sm ${
                                      (feedstock.change || '').toString().includes('-') ? 'text-rs-red' :
                                      (!(feedstock.change || '').toString().includes('-')) && feedstock.change !== '$0.00' && feedstock.change !== 'N/A' ? 'text-rs-dark-green' : ''
                                    }`}>
                                      {feedstock.change !== '$0.00' && feedstock.change !== 'N/A' ? feedstock.change : 'Flat'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))
                          )}
                        </div>

                        {/* Charts Section */}
                        <div className="relative flex flex-col items-center justify-center py-7 px-3 bg-rs-off-black rounded-lg gap-5 col-span-2 md:col-span-1">
                          {/* Bar Chart (optional) */}
                          {hasBarChart && (
                            <div className="w-full" style={{ height: 200 }}>
                              <canvas ref={barChartRef} className="w-full h-full" />
                            </div>
                          )}
                          {/* Line Chart */}
                          <div className="w-full" style={{ height: hasBarChart ? 250 : 300 }}>
                            <canvas ref={lineChartRef} className="w-full h-full" />
                          </div>
                          {chartLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-rs-off-black  backdrop-blur-[1px]">
                              <RefreshCw className="spinner !text-7xl opacity-40" />
                            </div>
                          )}
                          {showChemOrbisCredit && 
                            !chartLoading && !isDemoMode() && (
                                <div className="text-sm"><em>International Data Sourced by ChemOrbis</em></div>
                            )}
                        </div>

                        {/* Supply/Demand */}
                        {sdContent && (
                          <BlurInDemo
                            active={isDemoMode() && isFinalDataTitle(report.supplyDemandTitle)}
                            className="col-span-1 md:col-span-2 lg:col-start-3"
                          >
                            <ReportData
                              content={sdContent}
                              title={report.supplyDemandTitle || 'Supply/Demand'}
                              type="supplyDemand"
                            />
                          </BlurInDemo>
                        )}

                        {/* Conditional Feedstocks/International when action strategies exist but no supply/demand */}
                        {report.actionStrategies && !sdContent && (
                          <>
                            <ReportData
                              className={`lg:col-start-3 ${report.feedstocksBody.length > 0 && report.internationalBody.length <= 0 ? 'col-span-2' : 'col-span-2 md:col-span-1'}`}
                              content={report.feedstocksBody}
                              type="feedstocks"
                            />
                            <ReportData
                              className={`${report.internationalBody.length > 0 && report.internationalBody.feedstocksBody <= 0 ? 'lg:col-start-3 col-span-2' : 'col-span-2 md:col-span-1'}`}
                              content={report.internationalBody}
                              type="international"
                            />
                          </>
                        )}
                      </div>
                        );
                      })()}

                      {/* Spot Feeds — Full Weekly History */}
                      {!spotLoading && Object.keys(spotHistory).length > 0 && (
                        <div className="grid grid-cols-1 gap-4 mb-5">
                          {Object.entries(spotHistory).map(([title, hist]: [string, any]) => (
                            <div key={title} className="bg-rs-off-black rounded-lg p-5">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className={`text-sm font-bold p-2 rounded ${isDark ? 'text-rs-green' : 'text-black'}`}>
                                  {title} — Full History (Weekly)
                                </h4>
                                <button
                                  onClick={() => exportSpotHistoryCSV(title, hist)}
                                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded bg-rs-green text-black hover:opacity-80 transition-opacity"
                                  title="Export to CSV"
                                >
                                  <Download size={16} />
                                </button>
                              </div>
                              {hist.rows.length === 0 ? (
                                <div className="text-xs opacity-60">No historical data available.</div>
                              ) : (
                                <div data-demo-noscroll className="max-h-96 overflow-auto">
                                  <table className="w-full text-xs border-collapse">
                                    <thead className={`sticky top-0 z-10 ${isDark ? 'text-rs-green' : 'bg-rs-off-black text-black'}`}>
                                      <tr className="border-b-1 border-rs-jet">
                                        <th className="text-left py-2 px-2 font-bold whitespace-nowrap">Week (Fri)</th>
                                        {hist.feeds.map((f: any, i: number) => (
                                          <th key={i} className="text-right py-2 px-2 font-bold whitespace-nowrap">{f.label}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {hist.rows.map((row: any, ri: number) => (
                                        <tr key={ri} className={`border-b-1 border-rs-jet ${isDark ? 'text-white hover:text-black' : 'text-black'} hover:bg-rs-green transition-colors`}>
                                          <td className="text-left py-1.5 px-2 whitespace-nowrap">{formatFullDate(row.date)}</td>
                                          {row.values.map((v: string | null, ci: number) => (
                                            <td key={ci} className="text-right py-1.5 px-2 whitespace-nowrap">{v ?? '—'}</td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Final row for feedstocks/international when both action strategies and supply/demand exist */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                        {report.actionStrategies && !isOnTrial && report.supplyDemandBody && (
                          <>
                            <ReportData
                              className={`sm:col-span-2 ${report.internationalBody.length <= 0 ? 'col-span-1' : 'col-span-2 md:col-span-2'}`}
                              content={report.feedstocksBody}
                              type="feedstocks"
                            />
                            <ReportData
                              className="col-span-1 md:col-span-2"
                              content={
                                isDemoMode()
                                ? report.internationalBody.replace(/,?\s*according to (?:the )?ChemOrbis\b[^.,;]*/gi, '')
                                : report.internationalBody
                              }
                              type="international"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Drivers;
